import { useEffect, useState, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import api, { money, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { useModules } from "@/context/ModulesContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { SignaturePad } from "@/components/SignaturePad";
import {
  Truck, Wrench, Users, MapPin, Navigation, Play, Square, CheckCircle2, Check, X, Clock,
  DollarSign, PackageMinus, Route as RouteIcon, AlertTriangle, LogOut, Sun, ClipboardCheck, Leaf, ArrowLeft,
  Cloud, CloudRain, Megaphone, Camera, Trash2, Plus, PenLine, ChevronRight, MessageSquare, Coffee,
} from "lucide-react";
import L from "leaflet";
import "leaflet/dist/leaflet.css";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const fileUrl = (p) => `${API}/files/${p}?auth=${localStorage.getItem("nlc_token")}`;
const navLinks = (addr) => ({
  google: `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(addr || "")}`,
  waze: `https://waze.com/ul?q=${encodeURIComponent(addr || "")}&navigate=yes`,
  apple: `https://maps.apple.com/?q=${encodeURIComponent(addr || "")}`,
});

function decodePolyline(str, precision = 5) {
  let index = 0, lat = 0, lng = 0;
  const coords = [], factor = Math.pow(10, precision);
  while (index < str.length) {
    let result = 1, shift = 0, b;
    do { b = str.charCodeAt(index++) - 63 - 1; result += b << shift; shift += 5; } while (b >= 0x1f);
    lat += (result & 1) ? ~(result >> 1) : (result >> 1);
    result = 1; shift = 0;
    do { b = str.charCodeAt(index++) - 63 - 1; result += b << shift; shift += 5; } while (b >= 0x1f);
    lng += (result & 1) ? ~(result >> 1) : (result >> 1);
    coords.push([lat / factor, lng / factor]);
  }
  return coords;
}

function RouteMap({ depot, stops, geometry }) {
  const ref = useRef(null);
  const mapRef = useRef(null);
  useEffect(() => {
    if (!ref.current) return;
    if (!mapRef.current) mapRef.current = L.map(ref.current, { scrollWheelZoom: false });
    const map = mapRef.current;
    map.eachLayer((l) => map.removeLayer(l));
    L.tileLayer("https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png", { attribution: "© OpenStreetMap" }).addTo(map);
    const pts = [];
    const dot = (bg, txt) => L.divIcon({ className: "", html: `<div style="background:${bg};color:#fff;border-radius:50%;width:28px;height:28px;display:flex;align-items:center;justify-content:center;font-weight:700;font-size:13px;border:2px solid #fff;box-shadow:0 1px 3px rgba(0,0,0,.4)">${txt}</div>` });
    let drewRoad = false;
    if (geometry) {
      try {
        const road = decodePolyline(geometry);
        if (road.length > 1) { L.polyline(road, { color: "#16a34a", weight: 4, opacity: 0.85 }).addTo(map); road.forEach((p) => pts.push(p)); drewRoad = true; }
      } catch (e) { /* fall back to straight line */ }
    }
    if (!drewRoad) {
      const line = [depot && [depot.lat, depot.lon], ...(stops || []).map((s) => [s.lat, s.lon]), depot && [depot.lat, depot.lon]].filter(Boolean);
      if (line.length > 1) L.polyline(line, { color: "#16a34a", weight: 3, opacity: 0.5, dashArray: "6" }).addTo(map);
    }
    if (depot) { L.marker([depot.lat, depot.lon], { icon: dot("#0f172a", "H") }).addTo(map).bindPopup("Home base"); pts.push([depot.lat, depot.lon]); }
    (stops || []).forEach((s, i) => { L.marker([s.lat, s.lon], { icon: dot("#16a34a", i + 1) }).addTo(map).bindPopup(`${i + 1}. ${s.label}`); pts.push([s.lat, s.lon]); });
    if (pts.length) map.fitBounds(pts, { padding: [30, 30], maxZoom: 15 });
    setTimeout(() => map.invalidateSize(), 150);
  }, [depot, stops, geometry]);
  return <div ref={ref} data-testid="cad-map" className="h-72 lg:h-[68vh] w-full rounded-2xl overflow-hidden border-2 border-slate-100 z-0" />;
}
const todayStr = () => new Date().toISOString().slice(0, 10);
const mapsUrl = (addr) => `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(addr || "")}`;
const fmtDur = (m) => { const h = Math.floor(m / 60); const mm = Math.round(m % 60); return h > 0 ? `${h}h ${mm}m` : `${mm}m`; };
const statusPill = {
  scheduled: "bg-slate-200 text-slate-700", in_progress: "bg-amber-400 text-amber-950",
  completed: "bg-green-600 text-white", cancelled: "bg-red-200 text-red-700",
};

// A single OK / Fail checklist row used across start & end of day
function CheckRow({ r, onChange, testid }) {
  return (
    <div className="rounded-xl bg-white border-2 border-slate-100 p-3" data-testid={testid}>
      <div className="flex items-center justify-between gap-3">
        <span className="font-semibold text-slate-800">{r.item}</span>
        <div className="flex gap-2 shrink-0">
          <button type="button" onClick={() => onChange({ status: "ok", note: "" })} className={`h-11 w-14 rounded-lg font-bold flex items-center justify-center border-2 transition-colors ${r.status === "ok" ? "bg-green-600 text-white border-green-600" : "border-slate-200 text-slate-400"}`}><Check className="h-5 w-5" /></button>
          <button type="button" onClick={() => onChange({ status: "fail" })} className={`h-11 w-14 rounded-lg font-bold flex items-center justify-center border-2 transition-colors ${r.status === "fail" ? "bg-red-500 text-white border-red-500" : "border-slate-200 text-slate-400"}`}><X className="h-5 w-5" /></button>
        </div>
      </div>
      {r.status === "fail" && (
        <Textarea value={r.note} onChange={(e) => onChange({ note: e.target.value })} placeholder="What's wrong? (required)" className="mt-2" rows={2} />
      )}
    </div>
  );
}

export default function CAD() {
  const { user, ready, login, logout, isManager, isAdmin } = useAuth();
  const { isVisible } = useModules();
  const navigate = useNavigate();
  const date = todayStr();

  const [day, setDay] = useState(null);
  const [route, setRoute] = useState({ jobs: [], hours: [] });
  const [staff, setStaff] = useState([]);
  const [templates, setTemplates] = useState(null);
  const [now, setNow] = useState(Date.now());

  // start-of-day form
  const [vehicle, setVehicle] = useState("");
  const [crewSel, setCrewSel] = useState({});
  const [truckResp, setTruckResp] = useState([]);
  const [equipResp, setEquipResp] = useState([]);
  // end of day
  const [endOpen, setEndOpen] = useState(false);
  const [eodResp, setEodResp] = useState([]);
  const [eodNotes, setEodNotes] = useState("");
  // quick dialogs
  const [supOpen, setSupOpen] = useState(false);
  const [supplies, setSupplies] = useState([]);
  const [supId, setSupId] = useState("");
  const [supQty, setSupQty] = useState(1);
  const [issueOpen, setIssueOpen] = useState(false);
  const [issueText, setIssueText] = useState("");
  const [issueEquip, setIssueEquip] = useState("none");
  const [issueOOS, setIssueOOS] = useState(false);
  const [equipmentList, setEquipmentList] = useState([]);
  const [mileOpen, setMileOpen] = useState(false);
  const [miles, setMiles] = useState("");
  // extras
  const [weather, setWeather] = useState(null);
  const [announcements, setAnnouncements] = useState([]);
  const [annOpen, setAnnOpen] = useState(false);
  const [annText, setAnnText] = useState("");
  const [detailId, setDetailId] = useState(null);
  const [onWayJob, setOnWayJob] = useState(null);
  const [onBreak, setOnBreak] = useState(false);
  const [breakJob, setBreakJob] = useState(null);
  const [smsBusy, setSmsBusy] = useState(false);
  const [photoBusy, setPhotoBusy] = useState(false);
  const [sigValue, setSigValue] = useState("");
  const [sigName, setSigName] = useState("");
  const [mapData, setMapData] = useState(null);
  const [optimizing, setOptimizing] = useState(false);
  const [view, setView] = useState("home");
  const [homeTab, setHomeTab] = useState("dashboard");
  const [me, setMe] = useState(null);
  const autoRef = useRef(false);
  const nextRef = useRef(null);
  const [loginEmail, setLoginEmail] = useState("");
  const [loginPass, setLoginPass] = useState("");
  const [loginErr, setLoginErr] = useState("");

  const [loginBusy, setLoginBusy] = useState(false);
  const handleLogin = async (e) => {
    e.preventDefault();
    setLoginErr("");
    setLoginBusy(true);
    try {
      await login(loginEmail.trim(), loginPass);
    } catch (err) {
      setLoginErr(err.response?.data?.detail || "Invalid email or password");
    } finally {
      setLoginBusy(false);
    }
  };

  const loadDay = useCallback(() => api.get("/cad/day", { params: { date } }).then((r) => setDay(r.data)).catch(() => {}), [date]);
  const loadRoute = useCallback(() => api.get("/cad/route", { params: { date } }).then((r) => setRoute(r.data)).catch(() => {}), [date]);
  useEffect(() => {
    if (!user) return;
    loadDay(); loadRoute();
    api.get("/cad/staff").then((r) => setStaff(r.data)).catch(() => {});
    api.get("/cad/checklists").then((r) => setTemplates(r.data)).catch(() => {});
    api.get("/cad/weather").then((r) => setWeather(r.data)).catch(() => {});
    api.get("/announcements").then((r) => setAnnouncements(r.data)).catch(() => {});
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, [loadDay, loadRoute, user]);

  useEffect(() => {
    if (templates) {
      setTruckResp(templates.truck.map((i) => ({ item: i, status: "", note: "" })));
      setEquipResp(templates.equipment.map((i) => ({ item: i, status: "", note: "" })));
      setEodResp(templates.eod.map((i) => ({ item: i, status: "", note: "" })));
    }
  }, [templates]);

  useEffect(() => {
    if (day?.start_complete && !day?.end_complete && isManager && route.jobs.length > 0 && !mapData && !optimizing && !autoRef.current) {
      autoRef.current = true;
      optimize();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [day, route.jobs.length, isManager, mapData, optimizing]);

  const setResp = (setter) => (idx, patch) => setter((arr) => arr.map((x, i) => (i === idx ? { ...x, ...patch } : x)));

  const validChecks = (arr) => !arr.some((r) => !r.status || (r.status === "fail" && !r.note.trim()));

  const startDay = async () => {
    const crew_working = staff.filter((s) => crewSel[s.user_id]).map((s) => ({ user_id: s.user_id, name: s.name }));
    if (crew_working.length === 0) return toast.error("Pick who's working today");
    if (!validChecks(truckResp)) return toast.error("Finish the truck check (notes required on fails)");
    if (!validChecks(equipResp)) return toast.error("Finish the equipment check (notes required on fails)");
    try {
      await api.post("/cad/day/start", { date, vehicle, crew_working, truck_check: truckResp, equipment_check: equipResp });
      toast.success("Day started — let's roll!");
      autoRef.current = true;
      loadDay();
      optimize();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const endDay = async () => {
    if (!validChecks(eodResp)) return toast.error("Finish the end-of-day check");
    try {
      await api.post("/cad/day/end", { date, end_check: eodResp, notes: eodNotes });
      toast.success("Day closed. Nice work!");
      setEndOpen(false); loadDay();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const clockIn = async (jid, uid) => { try { await api.post(`/jobs/${jid}/crew/${uid}/clock-in`); toast.success("Clocked in"); loadRoute(); } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); } };
  const clockOut = async (jid, uid) => { try { await api.post(`/jobs/${jid}/crew/${uid}/clock-out`); toast.success("Time logged"); loadRoute(); } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); } };
  const loadMe = useCallback(() => api.get("/cad/me").then((r) => setMe(r.data)).catch(() => {}), []);
  useEffect(() => { if (user) loadMe(); }, [user, loadMe]);
  useEffect(() => { if (!user) return; const t = setInterval(loadMe, 20000); return () => clearInterval(t); }, [user, loadMe]);
  const dayClock = async (isIn) => {
    try {
      await api.post(`/cad/shift/clock-${isIn ? "in" : "out"}`);
      toast.success(isIn ? "Clocked in — have a great day!" : "Clocked out — day logged");
      loadMe();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  const optimize = async () => {
    setOptimizing(true);
    try { const { data } = await api.get("/cad/optimize", { params: { date } }); setMapData(data); await loadRoute(); toast.success("Route optimized — stops reordered"); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setOptimizing(false); }
  };
  const complete = async (jid) => { try { await api.put(`/jobs/${jid}/status`, { status: "completed" }); toast.success("Job completed"); loadRoute(); } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); } };

  const openDetail = (j) => { setDetailId(j.id); setSigValue(""); setSigName(user?.name || ""); };
  const uploadPhoto = async (kind, file) => {
    if (!file || !detailId) return;
    setPhotoBusy(true);
    try {
      const fd = new FormData(); fd.append("file", file);
      const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      await api.post(`/jobs/${detailId}/photos`, { path: data.path, kind });
      await loadRoute();
      toast.success(`${kind === "before" ? "Before" : "After"} photo added`);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setPhotoBusy(false); }
  };
  const delPhoto = async (pid) => { if (!detailId) return; await api.delete(`/jobs/${detailId}/photos/${pid}`); await loadRoute(); };
  const confirmComplete = async () => {
    if (!detailId) return;
    const job = (route.jobs || []).find((j) => j.id === detailId);
    const hasAfter = (job?.photos || []).some((p) => p.kind === "after");
    if (!hasAfter) return toast.error("Add an 'after' photo before completing this job.");
    if (!sigValue) return toast.error("Please add the employee signature to complete.");
    try {
      const { data } = await api.put(`/jobs/${detailId}/complete`, { signature: sigValue, signed_name: sigName });
      toast.success("Job completed");
      if (data?.sms?.ok) toast.success("Thank-you & review text sent to customer");
      if (data?.invoice?.wave?.ok) toast.success(`Invoice ${data.invoice.invoice_number} sent to Wave`);
      else if (data?.invoice && !data.invoice.wave?.ok) toast.message(`Invoice ${data.invoice.invoice_number} created (Wave: ${data.invoice.wave?.reason || "not synced"})`);
      setDetailId(null); setSigValue(""); loadRoute();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  const sendOnWay = async (job) => {
    if (!job) return;
    setSmsBusy(true);
    try {
      const { data } = await api.post("/sms/on-the-way", { job_id: job.id });
      if (data.ok) toast.success(`On-my-way text sent to ${job.customer_name || "customer"}`);
      else toast.error(data.reason || "Couldn't send text");
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setSmsBusy(false); setOnWayJob(null); }
  };
  const startBreak = (job) => { setBreakJob(job || null); setOnBreak(true); setOnWayJob(null); };
  const endBreak = () => { setOnBreak(false); if (breakJob) { setOnWayJob(breakJob); setBreakJob(null); } };
  const postAnnouncement = async () => {
    if (!annText.trim()) return;
    await api.post("/announcements", { text: annText });
    setAnnText(""); setAnnOpen(false);
    api.get("/announcements").then((r) => setAnnouncements(r.data));
  };
  const delAnnouncement = async (aid) => { await api.delete(`/announcements/${aid}`); api.get("/announcements").then((r) => setAnnouncements(r.data)); };

  const openSupplies = () => { api.get("/supplies").then((r) => { setSupplies(r.data); setSupId(r.data[0]?.id || ""); setSupOpen(true); }); };
  const logSupply = async () => {
    if (!supId) return;
    try { const { data } = await api.post(`/supplies/${supId}/use`, { quantity: Number(supQty) || 1 }); toast.success(`Logged — ${data.quantity} left, ${money(data.expense_amount)} expense`); setSupOpen(false); } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  const logIssue = async () => {
    if (!issueText.trim()) return;
    await api.post("/cad/issues", { text: issueText, date });
    if (issueEquip && issueEquip !== "none") {
      try {
        await api.post("/work-orders", { equipment_id: issueEquip, title: issueText, description: issueText, priority: "high", set_out_of_service: issueOOS });
      } catch (e) { toast.error("Office note sent, but the work order couldn't be created"); }
    }
    toast.success("Reported to the office");
    setIssueText(""); setIssueEquip("none"); setIssueOOS(false); setIssueOpen(false);
  };
  const openIssue = () => { api.get("/equipment").then((r) => setEquipmentList(r.data)).catch(() => {}); setIssueOpen(true); };
  const logMileage = async () => { if (!miles) return toast.error("Enter miles"); await api.post("/mileage", { date, miles: Number(miles), rate: 0.67, driver: user?.name }); toast.success("Mileage logged"); setMiles(""); setMileOpen(false); };

  // live hours computed from route jobs
  const liveHours = (() => {
    const map = {};
    for (const j of route.jobs) for (const c of j.crew || []) {
      if (!c.user_id) continue;
      const running = c.clock_in_at ? Math.max(0, (now - Date.parse(c.clock_in_at)) / 60000) : 0;
      const m = (Number(c.minutes) || 0) + running;
      const e = map[c.user_id] || { name: c.name, minutes: 0, rate: Number(c.hourly_rate) || 0, active: false };
      e.minutes += m; if (c.clock_in_at) e.active = true;
      map[c.user_id] = e;
    }
    return Object.values(map).sort((a, b) => b.minutes - a.minutes);
  })();

  const detail = detailId ? (route.jobs || []).find((j) => j.id === detailId) : null;
  const doneCount = route.jobs.filter((j) => j.status === "completed").length;
  const totalCount = route.jobs.length;
  const nextJob = route.jobs.find((j) => j.status !== "completed");
  const legByJob = (mapData?.stops || []).reduce((acc, s) => { acc[s.job_id] = s.leg_min; return acc; }, {});
  const onSiteMin = (j) => {
    const times = (j.crew || []).filter((c) => c.clock_in_at).map((c) => Date.parse(c.clock_in_at));
    return times.length ? Math.max(0, (now - Math.min(...times)) / 60000) : null;
  };
  useEffect(() => {
    if (nextRef.current) nextRef.current.scrollIntoView({ behavior: "smooth", block: "nearest" });
  }, [nextJob?.id]);
  const shell = (children) => (
    <div className="min-h-screen bg-slate-100" data-testid="cad-page">
      <header className="sticky top-0 z-20 bg-green-700 text-white px-4 py-3 flex items-center justify-between shadow-md">
        <div className="flex items-center gap-2">
          <div className="h-9 w-9 rounded-lg bg-white/15 flex items-center justify-center"><Leaf className="h-5 w-5" /></div>
          <div><div className="font-black tracking-tight leading-none">Field CAD</div><div className="text-[0.7rem] text-white/70">{new Date(date + "T00:00").toLocaleDateString(undefined, { weekday: "long", month: "short", day: "numeric" })}</div></div>
        </div>
        <div className="flex items-center gap-2">
          {view === "cad" && <Button size="sm" variant="ghost" className="text-white hover:bg-white/15" onClick={() => { setView("home"); loadMe(); }} data-testid="cad-to-home"><ArrowLeft className="h-4 w-4 mr-1" />Home</Button>}
          {(isAdmin || isManager) && <Button size="sm" variant="ghost" className="text-white hover:bg-white/15" onClick={() => navigate("/")} data-testid="cad-to-crm"><ArrowLeft className="h-4 w-4 mr-1" />CRM</Button>}
          <Button size="sm" variant="ghost" className="text-white hover:bg-white/15" onClick={logout} data-testid="cad-logout"><LogOut className="h-4 w-4" /></Button>
        </div>
      </header>
      <main className="px-4 sm:px-6 lg:px-10 py-4 max-w-7xl mx-auto space-y-4 pb-24">{children}</main>
    </div>
  );

  const stat = (label, val, Icon) => (
    <div className="rounded-2xl bg-white border-2 border-slate-100 p-4">
      <div className="flex items-center justify-between"><span className="text-[0.65rem] font-bold text-slate-400 uppercase tracking-wide">{label}</span><Icon className="h-4 w-4 text-green-600" /></div>
      <div className="font-black text-2xl text-slate-800 mt-1 tabular-nums">{val}</div>
    </div>
  );

  const employeeHome = () => shell(
    <div className="space-y-4" data-testid="employee-home">
      <div>
        <h1 className="font-black text-2xl text-slate-800">Hey {(user?.name || "there").split(" ")[0]}!</h1>
        <p className="text-slate-500 text-sm">Here's your day at Neighborhood Lawn Care.</p>
      </div>
      {me?.shift_active ? (
        <div className="rounded-2xl bg-green-600 text-white p-4 flex items-center justify-between" data-testid="home-clocked-banner">
          <div>
            <div className="flex items-center gap-2 font-black"><span className="h-2.5 w-2.5 rounded-full bg-white animate-pulse" />Clocked in for the day</div>
            <div className="text-white/80 text-sm mt-0.5">Started {fmtDur(Math.max(0, (now - Date.parse(me.shift_active.since)) / 60000))} ago</div>
          </div>
          <Button onClick={() => dayClock(false)} data-testid="home-banner-clockout" className="bg-white text-green-700 hover:bg-white/90 font-black h-11"><Square className="h-4 w-4 mr-1.5" />Clock Out</Button>
        </div>
      ) : (
        <div className="rounded-2xl bg-white border-2 border-slate-100 p-4 text-slate-500 text-sm" data-testid="home-clocked-banner">You're not clocked in. Open the Clock tab to start your day.</div>
      )}
      <div className="flex gap-2 bg-slate-200/60 p-1 rounded-xl">
        <button onClick={() => setHomeTab("dashboard")} data-testid="home-tab-dashboard" className={`flex-1 h-10 rounded-lg font-bold text-sm transition-colors ${homeTab === "dashboard" ? "bg-white text-green-700 shadow" : "text-slate-500"}`}>Dashboard</button>
        <button onClick={() => setHomeTab("clock")} data-testid="home-tab-clock" className={`flex-1 h-10 rounded-lg font-bold text-sm transition-colors ${homeTab === "clock" ? "bg-white text-green-700 shadow" : "text-slate-500"}`}>Clock In / Out</button>
      </div>
      {homeTab === "dashboard" ? (
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-3">
            {stat("Hours Today", fmtDur(me?.minutes_today || 0), Clock)}
            {stat("This Week", fmtDur(me?.minutes_week || 0), Sun)}
            {stat("Pay This Week", money(me?.pay_week || 0), DollarSign)}
            {stat("Jobs Today", String((me?.today_jobs || []).length), RouteIcon)}
          </div>
          <Button onClick={() => setView("cad")} data-testid="open-cad-btn" className="w-full h-16 text-lg font-black bg-green-600 hover:bg-green-700 rounded-2xl"><RouteIcon className="h-6 w-6 mr-2" />Open CAD — Today's Route</Button>
        </div>
      ) : (
        <div className="space-y-3" data-testid="home-clock-tab">
          <div className="grid grid-cols-2 gap-3">
            {stat("Hours Today", fmtDur(me?.minutes_today || 0), Clock)}
            {stat("This Week", fmtDur(me?.minutes_week || 0), Sun)}
          </div>
          {me?.shift_active ? (
            <Button onClick={() => dayClock(false)} data-testid="day-clock-out-btn" className="w-full h-16 text-lg font-black bg-slate-700 hover:bg-slate-800 rounded-2xl"><Square className="h-6 w-6 mr-2" />Clock Out — End My Day</Button>
          ) : (
            <Button onClick={() => dayClock(true)} data-testid="day-clock-in-btn" className="w-full h-16 text-lg font-black bg-green-600 hover:bg-green-700 rounded-2xl"><Play className="h-6 w-6 mr-2" />Clock In — Start My Day</Button>
          )}
          <p className="text-center text-xs text-slate-400">One clock-in at the start of your day and one clock-out at the end. Track time on each job inside the CAD route.</p>
        </div>
      )}
      <button onClick={() => setView("cad")} className="w-full text-center text-sm text-slate-400 pt-2 hover:text-green-700">or open the full field app →</button>
    </div>
  );

  // ---------- LOGIN GATE ----------
  if (ready && !user) {
    return (
      <div className="min-h-screen bg-green-700 flex items-center justify-center p-4" data-testid="cad-login-page">
        <div className="w-full max-w-md">
          <div className="text-center text-white mb-6">
            <div className="h-16 w-16 rounded-2xl bg-white/15 flex items-center justify-center mx-auto mb-3"><Leaf className="h-9 w-9" /></div>
            <h1 className="font-black text-3xl tracking-tight">Field CAD</h1>
            <p className="text-white/70">Neighborhood Lawn Care — Crew Login</p>
          </div>
          <form onSubmit={handleLogin} className="rounded-2xl bg-white p-6 space-y-4 shadow-xl" data-testid="cad-login-form">
            <div className="space-y-1.5">
              <Label>Email</Label>
              <Input type="email" value={loginEmail} onChange={(e) => setLoginEmail(e.target.value)} placeholder="you@nlc.com" autoComplete="username" className="h-12" data-testid="cad-login-email" />
            </div>
            <div className="space-y-1.5">
              <Label>Password</Label>
              <Input type="password" value={loginPass} onChange={(e) => setLoginPass(e.target.value)} placeholder="••••••••" autoComplete="current-password" className="h-12" data-testid="cad-login-password" />
            </div>
            {loginErr && <div className="text-sm font-semibold text-red-600" data-testid="cad-login-error">{loginErr}</div>}
            <Button type="submit" disabled={loginBusy} className="w-full h-14 text-lg font-black bg-green-600 hover:bg-green-700 rounded-xl" data-testid="cad-login-submit">
              {loginBusy ? "Signing in…" : "Log In"}
            </Button>
          </form>
        </div>
      </div>
    );
  }

  if (view === "home") return employeeHome();

  if (!ready || !day || !templates) return shell(<div className="text-center text-slate-400 py-20">Loading…</div>);

  // ---------- START OF DAY GATE ----------
  if (!day.start_complete) {
    if (!isManager) {
      return shell(
        <div className="rounded-2xl bg-white p-8 text-center border-2 border-slate-100" data-testid="cad-waiting">
          <Sun className="h-12 w-12 mx-auto text-amber-400 mb-3" />
          <h2 className="font-black text-xl text-slate-800">Waiting on your crew manager</h2>
          <p className="text-slate-500 mt-1">The day starts once the manager finishes the truck & equipment checks.</p>
        </div>
      );
    }
    return shell(
      <div className="space-y-5" data-testid="cad-start-form">
        <div className="rounded-2xl bg-green-700 text-white p-5"><h1 className="font-black text-2xl">Start the Day</h1><p className="text-white/80 text-sm">Complete every check before the crew can clock in.</p></div>

        <section className="rounded-2xl bg-white p-4 border-2 border-slate-100">
          <h3 className="font-bold text-slate-800 flex items-center gap-2 mb-3"><Users className="h-5 w-5 text-green-600" />Who's working today?</h3>
          <div className="flex flex-wrap gap-2">
            {staff.map((s) => (
              <button key={s.user_id} data-testid="crew-toggle" onClick={() => setCrewSel((c) => ({ ...c, [s.user_id]: !c[s.user_id] }))} className={`px-4 h-11 rounded-full font-semibold border-2 transition-colors ${crewSel[s.user_id] ? "bg-green-600 text-white border-green-600" : "bg-white text-slate-600 border-slate-200"}`}>{crewSel[s.user_id] && <Check className="h-4 w-4 inline mr-1" />}{s.name}</button>
            ))}
          </div>
          <div className="mt-3"><Label>Truck / Vehicle</Label><Input value={vehicle} onChange={(e) => setVehicle(e.target.value)} placeholder="Truck 1" className="mt-1" data-testid="cad-vehicle" /></div>
        </section>

        <section className="rounded-2xl bg-white p-4 border-2 border-slate-100">
          <h3 className="font-bold text-slate-800 flex items-center gap-2 mb-3"><Truck className="h-5 w-5 text-green-600" />Truck Check</h3>
          <div className="space-y-2">{truckResp.map((r, i) => <CheckRow key={i} r={r} testid={`truck-${i}`} onChange={(p) => setResp(setTruckResp)(i, p)} />)}</div>
        </section>

        <section className="rounded-2xl bg-white p-4 border-2 border-slate-100">
          <h3 className="font-bold text-slate-800 flex items-center gap-2 mb-3"><Wrench className="h-5 w-5 text-green-600" />Equipment Check</h3>
          <div className="space-y-2">{equipResp.map((r, i) => <CheckRow key={i} r={r} testid={`equip-${i}`} onChange={(p) => setResp(setEquipResp)(i, p)} />)}</div>
        </section>

        <Button onClick={startDay} data-testid="start-day-btn" className="w-full h-16 text-lg font-black bg-green-600 hover:bg-green-700 rounded-2xl shadow-lg"><Play className="h-6 w-6 mr-2" />Start the Day</Button>
      </div>
    );
  }

  // ---------- DAY CLOSED ----------
  if (day.end_complete) {
    return shell(
      <div className="rounded-2xl bg-white p-8 text-center border-2 border-slate-100" data-testid="cad-closed">
        <CheckCircle2 className="h-14 w-14 mx-auto text-green-600 mb-3" />
        <h2 className="font-black text-2xl text-slate-800">Day Closed</h2>
        <p className="text-slate-500 mt-1">Closed by {day.ended_by}. See you tomorrow!</p>
      </div>
    );
  }

  // ---------- ACTIVE DAY ----------
  return shell(
    <>
      {/* Weather + Announcements */}
      <div className="grid sm:grid-cols-2 gap-3">
        {isVisible("cad.weather") && weather?.available && (
          <div className="rounded-2xl bg-white border-2 border-slate-100 p-4 flex items-center gap-4" data-testid="cad-weather">
            {["Rain", "Drizzle", "showers", "Thunderstorm", "snow", "Snow"].some((w) => (weather.condition || "").includes(w)) ? <CloudRain className="h-9 w-9 text-blue-500" /> : (weather.condition === "Clear" || weather.condition === "Mainly clear" ? <Sun className="h-9 w-9 text-amber-400" /> : <Cloud className="h-9 w-9 text-slate-400" />)}
            <div>
              <div className="font-black text-slate-800">{weather.condition} · {weather.high}°/{weather.low}°F</div>
              <div className="text-sm text-slate-500">{weather.city} · {weather.precip}% chance of rain</div>
            </div>
          </div>
        )}
        {isAdmin && (
          <button onClick={() => setAnnOpen(true)} data-testid="post-announcement-btn" className="rounded-2xl bg-white border-2 border-dashed border-slate-200 p-4 flex items-center justify-center gap-2 text-slate-500 hover:border-green-300 font-semibold"><Megaphone className="h-5 w-5" />Post an announcement</button>
        )}
      </div>
      {isVisible("cad.announcements") && announcements.length > 0 && (
        <div className="space-y-2" data-testid="cad-announcements">
          {announcements.map((a) => (
            <div key={a.id} className="rounded-xl bg-amber-100 border-2 border-amber-200 text-amber-900 px-4 py-3 flex items-start gap-3">
              <Megaphone className="h-5 w-5 shrink-0 mt-0.5" />
              <div className="flex-1"><div className="font-semibold">{a.text}</div><div className="text-xs opacity-70">{a.created_by}</div></div>
              {isAdmin && <button onClick={() => delAnnouncement(a.id)} className="p-1 rounded hover:bg-amber-200"><Trash2 className="h-4 w-4" /></button>}
            </div>
          ))}
        </div>
      )}

      {isVisible("cad.crew") && (
      <section className="rounded-2xl bg-white p-4 border-2 border-slate-100">
        <div className="flex items-center justify-between"><span className="font-bold text-slate-800 flex items-center gap-2"><Users className="h-5 w-5 text-green-600" />On the crew</span>{day.vehicle && <span className="text-sm text-slate-500 flex items-center gap-1"><Truck className="h-4 w-4" />{day.vehicle}</span>}</div>
        <div className="flex flex-wrap gap-2 mt-2">{(day.crew_working || []).map((c) => <span key={c.user_id} className="px-3 h-9 rounded-full bg-green-50 text-green-700 font-semibold flex items-center text-sm">{c.name}</span>)}</div>
      </section>
      )}

      {/* Live hours */}
      {isVisible("cad.hours") && (
      <section className="rounded-2xl bg-white p-4 border-2 border-slate-100">
        <h3 className="font-bold text-slate-800 flex items-center gap-2 mb-2"><Clock className="h-5 w-5 text-green-600" />Today's Hours & Pay</h3>
        {liveHours.length === 0 ? <p className="text-slate-400 text-sm">No one clocked in yet.</p> : (
          <div className="space-y-1.5">{liveHours.map((h, i) => (
            <div key={i} className="flex items-center justify-between text-sm" data-testid="cad-hours-row">
              <span className="font-semibold text-slate-700 flex items-center gap-2">{h.active && <span className="h-2.5 w-2.5 rounded-full bg-green-500 animate-pulse" />}{h.name}</span>
              <span className="tabular-nums text-slate-500">{fmtDur(h.minutes)} · <span className="font-bold text-green-700">{money((h.minutes / 60) * h.rate)}</span></span>
            </div>
          ))}</div>
        )}
      </section>
      )}

      {/* Route: customers-to-do window (left) + sticky map (right) */}
      <section className="grid lg:grid-cols-2 gap-4 items-start">
        {/* LEFT — customers to do today */}
        <div className="rounded-2xl bg-white border-2 border-slate-100 p-4 space-y-3">
          <div className="flex items-center justify-between">
            <h3 className="font-black text-lg text-slate-800 flex items-center gap-2"><RouteIcon className="h-5 w-5 text-green-600" />Today's Route</h3>
            <span className="text-sm font-bold text-slate-500" data-testid="route-progress-label">{doneCount}/{totalCount} done</span>
          </div>
          {totalCount > 0 && (
            <div>
              <div className="h-2.5 rounded-full bg-slate-100 overflow-hidden"><div className="h-2.5 rounded-full bg-green-500 transition-all duration-500" style={{ width: `${totalCount ? (doneCount / totalCount) * 100 : 0}%` }} data-testid="route-progress-bar" /></div>
              <div className="text-xs text-slate-500 mt-1">{totalCount - doneCount} stop{totalCount - doneCount === 1 ? "" : "s"} left</div>
            </div>
          )}
          {nextJob && nextJob.address && (
            <a href={navLinks(nextJob.address).google} target="_blank" rel="noreferrer" data-testid="navigate-next-btn" className="flex items-center justify-center gap-2 h-12 rounded-xl bg-green-600 hover:bg-green-700 text-white font-black transition-colors"><Navigation className="h-5 w-5" />Navigate to Next: {nextJob.customer_name || nextJob.title}</a>
          )}
          {nextJob && (
            <button onClick={() => setOnWayJob(nextJob)} data-testid="on-my-way-btn" className="w-full flex items-center justify-center gap-2 h-11 rounded-xl border-2 border-green-600 text-green-700 font-bold hover:bg-green-50 transition-colors"><MessageSquare className="h-4 w-4" />On My Way — text customer</button>
          )}
          {totalCount === 0 ? (
            <div className="p-8 text-center text-slate-400">No jobs scheduled today.</div>
          ) : (
            <div className="space-y-2">
              {route.jobs.map((j, idx) => {
                const isNext = nextJob && j.id === nextJob.id;
                const leg = legByJob[j.id];
                const site = onSiteMin(j);
                const isDone = j.status === "completed";
                return (
                  <button key={j.id} ref={isNext ? nextRef : null} data-testid="route-card" onClick={() => openDetail(j)} className={`w-full text-left rounded-2xl border-2 p-3 flex items-center gap-3 transition-colors ${isNext ? "border-green-500 bg-green-50 ring-2 ring-green-500/30" : "border-slate-100 bg-white hover:border-green-300"}`}>
                    <div className={`h-9 w-9 rounded-full text-white font-black flex items-center justify-center shrink-0 ${isDone ? "bg-slate-300" : "bg-green-600"}`}>{isDone ? <Check className="h-5 w-5" /> : idx + 1}</div>
                    <div className="flex-1 min-w-0">
                      <div className="font-black text-slate-800 truncate flex items-center gap-2">{j.customer_name || j.title}{isNext && <span className="px-1.5 py-0.5 rounded bg-green-600 text-white text-[0.6rem] font-bold tracking-wide shrink-0">NEXT</span>}</div>
                      {j.address && <div className="text-sm text-slate-500 flex items-center gap-1 truncate"><MapPin className="h-3.5 w-3.5 shrink-0" />{j.address}</div>}
                      {(leg != null || (site != null && !isDone)) && (
                        <div className="flex items-center gap-3 mt-0.5 text-xs font-semibold">
                          {leg != null && <span className="text-slate-400 flex items-center gap-1"><RouteIcon className="h-3 w-3" />{leg} min drive</span>}
                          {site != null && !isDone && <span className="text-green-600 flex items-center gap-1"><Clock className="h-3 w-3" />on site {fmtDur(site)}</span>}
                        </div>
                      )}
                    </div>
                    <span className={`px-2.5 py-1 rounded-full text-xs font-bold capitalize shrink-0 ${statusPill[j.status] || statusPill.scheduled}`}>{(j.status || "").replace("_", " ")}</span>
                    <ChevronRight className="h-5 w-5 text-slate-300 shrink-0" />
                  </button>
                );
              })}
            </div>
          )}
        </div>

        {/* RIGHT — sticky map */}
        <div className="lg:sticky lg:top-20 space-y-2">
          <div className="flex items-center justify-between gap-2 px-1">
            <div className="text-sm font-semibold text-slate-700 flex items-center gap-4" data-testid="route-summary">
              {mapData?.summary ? (<><span className="flex items-center gap-1"><RouteIcon className="h-4 w-4 text-green-600" />{mapData.summary.distance_mi} mi</span><span className="flex items-center gap-1"><Clock className="h-4 w-4 text-green-600" />~{mapData.summary.duration_min} min drive</span></>) : <span className="text-slate-400">Optimize to see the route</span>}
            </div>
            {isManager && totalCount > 0 && <Button size="sm" onClick={optimize} disabled={optimizing} data-testid="optimize-route-btn" className="bg-green-600 hover:bg-green-700 shrink-0"><RouteIcon className="h-4 w-4 mr-1.5" />{optimizing ? "Optimizing…" : "Optimize"}</Button>}
          </div>
          {mapData ? <RouteMap depot={mapData.depot} stops={mapData.stops} geometry={mapData.geometry} /> : <div className="h-72 lg:h-[68vh] rounded-2xl border-2 border-dashed border-slate-200 flex items-center justify-center text-slate-400 text-sm text-center px-6">Map appears here once the route is optimized</div>}
        </div>
      </section>

      {/* Quick actions */}
      {isVisible("cad.quick_actions") && (
      <section className="grid grid-cols-3 gap-3">
        <button onClick={openSupplies} data-testid="cad-supplies" className="rounded-2xl bg-white border-2 border-slate-100 p-4 flex flex-col items-center gap-2 hover:border-green-300"><PackageMinus className="h-6 w-6 text-green-600" /><span className="text-xs font-bold text-slate-600">Supplies Used</span></button>
        <button onClick={() => setMileOpen(true)} data-testid="cad-mileage" className="rounded-2xl bg-white border-2 border-slate-100 p-4 flex flex-col items-center gap-2 hover:border-green-300"><RouteIcon className="h-6 w-6 text-green-600" /><span className="text-xs font-bold text-slate-600">Log Mileage</span></button>
        <button onClick={openIssue} data-testid="cad-issue" className="rounded-2xl bg-white border-2 border-slate-100 p-4 flex flex-col items-center gap-2 hover:border-green-300"><AlertTriangle className="h-6 w-6 text-amber-500" /><span className="text-xs font-bold text-slate-600">Report Issue</span></button>
      </section>
      )}

      {isManager && (
        <Button onClick={() => setEndOpen(true)} data-testid="end-day-btn" className="w-full h-14 text-lg font-black bg-slate-800 hover:bg-slate-900 rounded-2xl"><ClipboardCheck className="h-5 w-5 mr-2" />End of Day Check-off</Button>
      )}

      {/* Break mode overlay */}
      {onBreak && (
        <div className="fixed inset-0 z-[60] bg-slate-900/95 flex flex-col items-center justify-center text-white p-6" data-testid="break-overlay">
          <Coffee className="h-16 w-16 mb-4 text-green-400" />
          <h2 className="font-black text-3xl">On Break</h2>
          <p className="text-white/70 mt-2 text-center max-w-xs">The day is paused. When you end your break we'll ask if you want to text the customer you're on the way.</p>
          <Button onClick={endBreak} data-testid="end-break-btn" className="mt-8 h-14 px-10 text-lg font-black bg-green-600 hover:bg-green-700 rounded-xl">End Break</Button>
        </div>
      )}

      {/* On My Way dialog */}
      <Dialog open={!!onWayJob} onOpenChange={(o) => !o && setOnWayJob(null)}>
        <DialogContent className="max-w-sm" data-testid="on-my-way-dialog">
          <DialogHeader>
            <DialogTitle>Tell {onWayJob?.customer_name || "the customer"} we're on the way?</DialogTitle>
            <DialogDescription>{onWayJob?.address}</DialogDescription>
          </DialogHeader>
          <div className="space-y-2">
            <Button onClick={() => sendOnWay(onWayJob)} disabled={smsBusy} data-testid="onway-send-btn" className="w-full h-12 font-black bg-green-600 hover:bg-green-700"><MessageSquare className="h-5 w-5 mr-2" />{smsBusy ? "Sending…" : "Yes, send text now"}</Button>
            <Button onClick={() => startBreak(onWayJob)} variant="outline" data-testid="onway-break-btn" className="w-full h-12 font-bold border-2"><Coffee className="h-5 w-5 mr-2" />Going on break — text later</Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Job detail dialog — all info + actions */}
      <Dialog open={!!detail} onOpenChange={(o) => !o && setDetailId(null)}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid="cad-job-detail">
          {detail && (
            <>
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2 flex-wrap">
                  {detail.customer_name || detail.title}
                  <span className={`px-2 py-0.5 rounded-full text-xs font-bold capitalize ${statusPill[detail.status] || statusPill.scheduled}`}>{(detail.status || "").replace("_", " ")}</span>
                </DialogTitle>
                <DialogDescription>{detail.start_time}{detail.start_time ? " · " : ""}{detail.title}{detail.price ? ` · ${money(detail.price)}` : ""}</DialogDescription>
              </DialogHeader>
              <div className="space-y-4">
                {detail.address && (
                  <div className="rounded-xl bg-slate-50 border-2 border-slate-100 p-3">
                    <div className="text-sm font-semibold text-slate-700 flex items-center gap-1.5"><MapPin className="h-4 w-4 text-green-600 shrink-0" />{detail.address}</div>
                    <div className="flex gap-2 mt-2" data-testid="cad-navigate">
                      <a href={navLinks(detail.address).google} target="_blank" rel="noreferrer" className="flex-1 text-center h-10 rounded-lg bg-green-600 text-white font-bold flex items-center justify-center text-sm">Maps</a>
                      <a href={navLinks(detail.address).waze} target="_blank" rel="noreferrer" className="flex-1 text-center h-10 rounded-lg bg-blue-500 text-white font-bold flex items-center justify-center text-sm">Waze</a>
                      <a href={navLinks(detail.address).apple} target="_blank" rel="noreferrer" className="flex-1 text-center h-10 rounded-lg bg-slate-700 text-white font-bold flex items-center justify-center text-sm">Apple</a>
                    </div>
                  </div>
                )}
                {detail.notes && <div className="rounded-lg bg-amber-50 text-amber-800 text-sm px-3 py-2 flex gap-2"><AlertTriangle className="h-4 w-4 shrink-0 mt-0.5" />{detail.notes}</div>}

                <div className="space-y-2">
                  <div className="font-bold text-slate-800 flex items-center gap-2"><Users className="h-4 w-4 text-green-600" />Crew & Time</div>
                  {(detail.crew || []).map((c) => (
                    <div key={c.user_id} className="flex items-center justify-between gap-2">
                      <span className="text-sm font-semibold text-slate-600 flex items-center gap-2">{c.clock_in_at && <span className="h-2 w-2 rounded-full bg-green-500 animate-pulse" />}{c.name}</span>
                      {c.clock_in_at
                        ? <Button size="sm" data-testid="cad-clock-out" onClick={() => clockOut(detail.id, c.user_id)} className="h-10 bg-slate-700 hover:bg-slate-800"><Square className="h-4 w-4 mr-1" />Clock Out</Button>
                        : <Button size="sm" data-testid="cad-clock-in" onClick={() => clockIn(detail.id, c.user_id)} className="h-10 bg-green-600 hover:bg-green-700"><Play className="h-4 w-4 mr-1" />Clock In</Button>}
                    </div>
                  ))}
                  {(detail.crew || []).length === 0 && <p className="text-xs text-slate-400">No crew assigned to this job.</p>}
                </div>

                <div className="space-y-2">
                  {["before", "after"].map((kind) => (
                    <div key={kind} className="space-y-1.5">
                      <div className="flex items-center justify-between">
                        <Label className="capitalize text-sm">{kind} photos</Label>
                        <label className="text-sm font-bold text-green-700 cursor-pointer flex items-center gap-1"><Plus className="h-4 w-4" />Add<input type="file" accept="image/*" className="hidden" data-testid={`cad-photo-${kind}`} onChange={(e) => uploadPhoto(kind, e.target.files?.[0])} /></label>
                      </div>
                      <div className="flex flex-wrap gap-2">
                        {(detail.photos || []).filter((p) => p.kind === kind).map((p) => (
                          <div key={p.id} className="relative">
                            <img src={fileUrl(p.path)} alt="" className="h-16 w-16 rounded-lg object-cover border-2 border-slate-100" />
                            <button onClick={() => delPhoto(p.id)} className="absolute -top-1.5 -right-1.5 h-5 w-5 rounded-full bg-red-500 text-white flex items-center justify-center"><X className="h-3 w-3" /></button>
                          </div>
                        ))}
                        {(detail.photos || []).filter((p) => p.kind === kind).length === 0 && <span className="text-xs text-slate-400">None yet.</span>}
                      </div>
                    </div>
                  ))}
                  {photoBusy && <p className="text-xs text-slate-400">Uploading…</p>}
                </div>

                {detail.status !== "completed" ? (
                  <div className="space-y-2 border-t-2 border-slate-100 pt-3">
                    <Label className="flex items-center gap-1.5"><PenLine className="h-4 w-4" />Employee signature</Label>
                    <SignaturePad onChange={setSigValue} />
                    <Input value={sigName} onChange={(e) => setSigName(e.target.value)} placeholder="Employee name" data-testid="cad-sign-name" />
                    <Button onClick={confirmComplete} data-testid="cad-confirm-complete" className="w-full h-12 font-black bg-green-600 hover:bg-green-700"><CheckCircle2 className="h-5 w-5 mr-1.5" />Mark Complete</Button>
                  </div>
                ) : (
                  <div className="rounded-xl bg-green-50 text-green-700 font-bold text-center py-3 flex items-center justify-center gap-2"><CheckCircle2 className="h-5 w-5" />Job completed</div>
                )}
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Announcement dialog */}
      <Dialog open={annOpen} onOpenChange={setAnnOpen}>
        <DialogContent className="max-w-sm">
          <DialogHeader><DialogTitle>Post Announcement</DialogTitle><DialogDescription>Shows on every crew's CAD today.</DialogDescription></DialogHeader>
          <Textarea value={annText} onChange={(e) => setAnnText(e.target.value)} rows={3} placeholder="e.g. Team meeting 7am tomorrow. Bring the trailer." data-testid="announcement-text" />
          <DialogFooter><Button variant="ghost" onClick={() => setAnnOpen(false)}>Cancel</Button><Button onClick={postAnnouncement} data-testid="announcement-save" className="bg-green-600 hover:bg-green-700">Post</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      {/* End of day dialog */}
      <Dialog open={endOpen} onOpenChange={setEndOpen}>
        <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>End of Day Check-off</DialogTitle><DialogDescription>Required to close out the day.</DialogDescription></DialogHeader>
          <div className="space-y-2">{eodResp.map((r, i) => <CheckRow key={i} r={r} testid={`eod-${i}`} onChange={(p) => setResp(setEodResp)(i, p)} />)}</div>
          <Textarea value={eodNotes} onChange={(e) => setEodNotes(e.target.value)} placeholder="End of day notes (optional)" rows={2} className="mt-2" />
          <DialogFooter><Button variant="ghost" onClick={() => setEndOpen(false)}>Cancel</Button><Button onClick={endDay} data-testid="confirm-end-day" className="bg-green-600 hover:bg-green-700">Close the Day</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Supplies dialog */}
      <Dialog open={supOpen} onOpenChange={setSupOpen}>
        <DialogContent className="max-w-sm">
          <DialogHeader><DialogTitle>Log Supplies Used</DialogTitle><DialogDescription>Reduces stock & logs a materials expense.</DialogDescription></DialogHeader>
          <div className="space-y-3">
            <div className="space-y-1.5"><Label>Item</Label>
              <Select value={supId} onValueChange={setSupId}><SelectTrigger data-testid="cad-supply-select"><SelectValue placeholder="Pick a supply" /></SelectTrigger><SelectContent>{supplies.map((s) => <SelectItem key={s.id} value={s.id}>{s.name} ({s.quantity} {s.unit} left)</SelectItem>)}</SelectContent></Select>
            </div>
            <div className="space-y-1.5"><Label>How many used?</Label><Input type="number" value={supQty} onChange={(e) => setSupQty(e.target.value)} data-testid="cad-supply-qty" /></div>
          </div>
          <DialogFooter><Button variant="ghost" onClick={() => setSupOpen(false)}>Cancel</Button><Button onClick={logSupply} data-testid="cad-supply-save" className="bg-green-600 hover:bg-green-700">Log</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Mileage dialog */}
      <Dialog open={mileOpen} onOpenChange={setMileOpen}>
        <DialogContent className="max-w-sm">
          <DialogHeader><DialogTitle>Log Mileage</DialogTitle><DialogDescription>End-of-day miles for the truck.</DialogDescription></DialogHeader>
          <div className="space-y-1.5"><Label>Miles driven today</Label><Input type="number" value={miles} onChange={(e) => setMiles(e.target.value)} data-testid="cad-miles" /></div>
          <DialogFooter><Button variant="ghost" onClick={() => setMileOpen(false)}>Cancel</Button><Button onClick={logMileage} data-testid="cad-mileage-save" className="bg-green-600 hover:bg-green-700">Save</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Issue dialog */}
      <Dialog open={issueOpen} onOpenChange={setIssueOpen}>
        <DialogContent className="max-w-sm">
          <DialogHeader><DialogTitle>Report an Issue</DialogTitle><DialogDescription>Sends a note to the office. Attach a piece of equipment to open a work order.</DialogDescription></DialogHeader>
          <Textarea value={issueText} onChange={(e) => setIssueText(e.target.value)} placeholder="e.g. Mower #2 won't start, customer gate locked…" rows={4} data-testid="cad-issue-text" />
          <div className="space-y-1.5">
            <Label className="text-xs font-bold text-slate-500">Equipment (optional)</Label>
            <Select value={issueEquip} onValueChange={setIssueEquip}>
              <SelectTrigger data-testid="cad-issue-equipment"><SelectValue placeholder="None" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="none">None</SelectItem>
                {equipmentList.map((eq) => <SelectItem key={eq.id} value={eq.id}>{eq.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          {issueEquip !== "none" && (
            <label className="flex items-center gap-2 text-sm text-slate-600" data-testid="cad-issue-oos">
              <input type="checkbox" checked={issueOOS} onChange={(e) => setIssueOOS(e.target.checked)} className="h-4 w-4" />
              Mark this equipment out of service
            </label>
          )}
          <DialogFooter><Button variant="ghost" onClick={() => setIssueOpen(false)}>Cancel</Button><Button onClick={logIssue} data-testid="cad-issue-save" className="bg-amber-500 hover:bg-amber-600">Send</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
